/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { videoDirective } from '../../directives/video.directive';

/**
 * Video pilot with configurable behaviors used by video intersection presets.
 */
export const videoPilot = {
  /**
   * Trigger when video element visible ratio larger than configured `threshold`
   *
   * Supported behaviors:
   *
   *  - `init` Auto init video
   *  - `autoplay` Auto play the video when video visible
   *  - `resume` Resume the playback when video visible
   *
   * For example:
   *
   *  1. Behaviors: [`init`, `autoplay`]
   *      - `init` the video if it's not initialized
   *      - `autoplay` the video after initialized *(muted video is required)*
   *
   *  2. Behaviors: [`init`, `resume`]
   *      - `init` the video if it's not initialized
   *      - Don't play the video after initialized
   *      - After user click play the video and it get paused when hidden
   *      - The video will `resume` to playing when get back to visible
   *
   * @param {HTMLElement} element   The video element
   * @param {string[]}    behaviors List of behavior names when
   */
  enter(element, behaviors = ['init', 'autoplay']) {
    const video = videoDirective.videos.get(element.ambid);

    if (video && !video.isDestroyed()) {
      video.visible = true;

      if (!video.isInitialized() && behaviors.includes('init')) {
        video.init()
          .then(player => video.visible && !video.isDestroyed() && behaviors.includes('autoplay') && player.play())
          .catch(error => video.handleError(error))
        ;
      } else if (video.player) {
        if (behaviors.includes('autoplay')) {
          // Abort if leave() already hid the element (banner play-token / slide change).
          const play = () => {
            if (!video.visible || video.isDestroyed()) {
              return;
            }

            video.player.play().catch(error => video.handleError(error));
          };

          // Always rewind before play — after ENDED (or leave/pause races) play() often no-ops.
          try {
            if (video.player.type === 'youtube' && video.player.player && window.YT) {
              try {
                video.player.player.seekTo(0, true);
              } catch (e) {
                // ignore seek failures; still attempt play
              }

              play();
            } else if (video.player.type === 'vimeo') {
              video.player.call('setCurrentTime', 0)
                .catch(() => {})
                .then(play, play)
                .catch(error => video.handleError(error));
            } else {
              play();
            }
          } catch (e) {
            play();
          }
        } else if (behaviors.includes('resume')) {
          video.player
            .call('getCurrentTime')
            .then(seconds => video.visible && !video.isDestroyed() && seconds > 0 && video.player.play())
            .catch(error => video.handleError(error))
          ;
        }
      }
    }
  },

  /**
   * Trigger when video element visible ratio lower or equal configured `threshold`
   *
   * Supported behaviors:
   *
   *  - `autopause` Auto pause the video when video hidden
   *
   * @param {HTMLElement} element   The video element
   * @param {string[]}    behaviors List of behavior names when
   */
  leave(element, behaviors = ['autopause']) {
    const video = videoDirective.videos.get(element.ambid);

    if (video && !video.isDestroyed()) {
      video.visible = false;

      if (video.isInitialized() && behaviors.includes('autopause')) {
        video.player.pause().catch(error => video.handleError(error));
      }
    }
  }
};
